import { useSearchParams } from "react-router-dom";

export interface UseSearchParamOptions {
	key: string;
	defaultValue: string;
}

/**
 * useSearchParam Hook
 *
 * A custom React Hook for managing and interacting with a specific search parameter in the URL.
 * This hook is designed to work with the `useSearchParams` hook from the "react-router-dom" library.
 *
 * @param {UseSearchParamOptions} options - Configuration options for the search parameter.
 * @param {string} options.key - The key of the search parameter to be managed.
 * @param {string} options.defaultValue - The default value to be used when the search parameter is not present in the URL.
 *
 * @returns {[string, (value: string) => void]} - A tuple containing the current value of the search parameter
 * and a function to update the value in the URL.
 *
 * @example
 * // Usage example:
 * const ExampleComponent = () => {
 *   const [searchParam, setSearchParam] = useSearchParam({ key: "example", defaultValue: "default" });
 *
 *   // Access the current value of the search parameter
 *   console.log("Current Search Parameter Value:", searchParam);
 *
 *   // Update the search parameter value
 *   setSearchParam("new-value");
 * };
 */
const useSearchParam = ({
	key,
	defaultValue
}: UseSearchParamOptions): [string, (value: string) => void] => {
	const [searchParams, setSearchParams] = useSearchParams();

	const searchParam = searchParams.get(key) ?? defaultValue;

	const setParam = (value: string) => {
		const previousParams = Object.fromEntries([...searchParams]);
		setSearchParams({ ...previousParams, [key]: value });
	};

	return [searchParam, setParam];
};

export default useSearchParam;
